import { Icon } from "@introist/react-foundation/v2";
import { AppMenuItem } from "../shell";
import { ASSISTANT_ROUTES } from "../../../assistant";
import { appRoutes, buildSubRoute, SETTINGS_APP_MENU_ITEMS } from "../../../AppRoutes";
import { DATASET_ROUTES } from "../../../modules/datasets";
import { Database } from "lucide-react";
import { BATCHES_ROUTES } from "../../../modules/batches";
import { NUDGES_ROUTES } from "../../../modules/nudges";
import { TRIGGER_ROUTES } from "../../../modules/triggers";
import { WORKFLOWS_ROUTES } from "../../../modules/flows";
import { FORMS_ROUTES } from "../../../modules/forms";
import { TASKS_ROUTES } from "../../../modules/tasks";
import { REPORTS_ROUTES } from "../../../modules/reports";
import { ANALYTICS_ROUTES } from "../../../modules/analytics";
import { SEARCH_ROUTES } from "../../../modules/search";
import { EMPLOYEE_ROUTES } from "../../../modules/employees";
import { COMPANY_DATA_ROUTES } from "../../../modules/companydata";
import { DATA_SOURCE_ROUTES } from "../../../modules/datasources";
import { HOLIDAY_CALENDAR_ROUTES } from "../../../modules/holiday-calendar";
import { TEMPLATES_ROUTES } from "../../../modules/templates";
import { useHasScope, useHasRole, useIdentity, UserRole } from "../../../IdentityProvider";
import { SYSTEM_ROUTES } from "../../../modules/system";
import { useFeatureFlag } from "../../../hooks";
import { useDatasetDefs } from "../../../modules/datasets/hooks/useDatasetDefs";

export const useAutomationStudioMenuItems = (
  handleItemClick: (to: string) => (e: React.MouseEvent) => unknown
) => {
  let items: AppMenuItem[] = [];

  const { datasets } = useDatasetDefs();

  const { user } = useIdentity();
  // Restricted (non-admin) users only ever see Workflows, gated on holding any
  // workflows:* scope - everything else in this menu is admin+-only for now.
  const isAdmin = useHasRole(UserRole.Admin);
  const hasWorkflowsScope = useHasScope([
    "workflows:read",
    "workflows:read_all",
    "workflows:write",
    "workflows:write_all"
  ]);

  const hasLegacy = useFeatureFlag("legacy").isEnabled;
  const hasDatasets = useFeatureFlag("datasets").isEnabled;

  const hasOnboardingModule = useFeatureFlag("module-onboarding").isEnabled;
  const hasForms = useFeatureFlag("forms").isEnabled;
  const hasTasks = useFeatureFlag("tasks").isEnabled;
  const hasReports = useFeatureFlag("reports").isEnabled;
  const hasLibrary = useFeatureFlag("library").isEnabled;
  const hasAssistant = useFeatureFlag("assistant").isEnabled;

  if (isAdmin && hasAssistant) {
    items.push({
      title: "Assistant",
      path: ASSISTANT_ROUTES.assistant.path,
      icon: ASSISTANT_ROUTES.assistant.icon,
      divider: true,
      onClick: handleItemClick(ASSISTANT_ROUTES.assistant.path)
    });
  }

  if (isAdmin && hasOnboardingModule) {
    items.push({
      title: "Onboarding",
      path: appRoutes.onboardingRoot.path,
      icon: <Icon name="personAdd" />,
      divider: true,
      onClick: handleItemClick(appRoutes.onboardingRoot.path)
    });
  }

  if (isAdmin) {
    items.push({
      title: "Employees",
      path: appRoutes.employees.path,
      icon: appRoutes.employees.icon,
      onClick: handleItemClick(appRoutes.employees.path)
    });
  }

  if (isAdmin && datasets && datasets.length > 0) {
    const datasetSubItems = datasets.map(dataset => ({
      key: dataset.id,
      title: dataset.plural,
      path: DATASET_ROUTES.dataset.path.replace(":id", dataset.id),
      onClick: handleItemClick(DATASET_ROUTES.dataset.path.replace(":id", dataset.id))
    }));

    items.push({
      title: "Data",
      icon: <Database size={17} />,
      closable: false,
      subItems: datasetSubItems
    });
  }

  if (isAdmin) {
    items.push({
      title: "Automations",
      path: appRoutes.automations.path,
      icon: <Icon name="condition" />,
      onClick: handleItemClick(appRoutes.automations.path),
      divider: true
    });

    items.push({
      title: "Batches",
      path: BATCHES_ROUTES.batches.path,
      icon: BATCHES_ROUTES.batches.icon,
      onClick: handleItemClick(BATCHES_ROUTES.batches.path)
    });

    items.push({
      title: "Nudges",
      path: NUDGES_ROUTES.nudges.path,
      icon: NUDGES_ROUTES.nudges.icon,
      onClick: handleItemClick(NUDGES_ROUTES.nudges.path),
      divider: true
    });

    items.push({
      title: TRIGGER_ROUTES.triggers.title || "Triggers",
      icon: TRIGGER_ROUTES.triggers.icon,
      path: TRIGGER_ROUTES.triggers.path,
      onClick: handleItemClick(TRIGGER_ROUTES.triggers.path)
    });
  }

  if (hasWorkflowsScope) {
    items.push({
      title: WORKFLOWS_ROUTES.flows.title || "Workflows",
      icon: WORKFLOWS_ROUTES.flows.icon,
      path: WORKFLOWS_ROUTES.flows.path,
      onClick: handleItemClick(WORKFLOWS_ROUTES.flows.path)
    });
  }

  if (isAdmin) {
    items.push({
      title: "Activation map",
      icon: <Icon name="trigger" />,
      path: "/activation-map",
      onClick: handleItemClick("/activation-map"),
      divider: !hasLibrary
    });

    if (hasLibrary) {
      items.push({
        title: TEMPLATES_ROUTES.templates.title || "Library",
        icon: TEMPLATES_ROUTES.templates.icon,
        path: TEMPLATES_ROUTES.templates.path,
        onClick: handleItemClick(TEMPLATES_ROUTES.templates.path),
        divider: true
      });
    }

    items.push({
      title: appRoutes.events.title!,
      icon: appRoutes.events.icon,
      path: appRoutes.events.path,
      onClick: handleItemClick(appRoutes.events.path)
    });

    if (hasForms) {
      items.push({
        title: "Forms",
        icon: FORMS_ROUTES.forms.icon,
        path: FORMS_ROUTES.forms.path,
        onClick: handleItemClick(FORMS_ROUTES.forms.path)
      });
    }

    if (hasTasks) {
      items.push({
        title: "Tasks",
        icon: TASKS_ROUTES.tasks.icon,
        path: TASKS_ROUTES.tasks.path,
        onClick: handleItemClick(TASKS_ROUTES.tasks.path),
        divider: !hasLegacy
      });
    }

    if (hasLegacy) {
      items.push({
        title: appRoutes.materials.title!,
        path: appRoutes.materials.path,
        icon: appRoutes.materials.icon,
        onClick: handleItemClick(appRoutes.materials.path),
        divider: true
      });
    }

    if (hasReports) {
      items.push({
        title: REPORTS_ROUTES.reports.title!,
        path: REPORTS_ROUTES.reports.path,
        icon: REPORTS_ROUTES.reports.icon,
        onClick: handleItemClick(REPORTS_ROUTES.reports.path)
      });
    }

    items.push({
      title: ANALYTICS_ROUTES.analytics.title!,
      path: ANALYTICS_ROUTES.analytics.path,
      icon: ANALYTICS_ROUTES.analytics.icon,
      onClick: handleItemClick(ANALYTICS_ROUTES.analytics.path)
    });

    items.push({
      title: SEARCH_ROUTES.search.title!,
      path: SEARCH_ROUTES.search.path,
      icon: SEARCH_ROUTES.search.icon,
      onClick: handleItemClick(SEARCH_ROUTES.search.path),
      divider: true
    });

    const dataSubItems = [
      {
        title: "Employee Fields",
        path: EMPLOYEE_ROUTES.employeeFields.path,
        onClick: handleItemClick(EMPLOYEE_ROUTES.employeeFields.path)
      },
      {
        title: "Company Data",
        path: COMPANY_DATA_ROUTES.companyData.path,
        onClick: handleItemClick(COMPANY_DATA_ROUTES.companyData.path)
      },
      {
        title: "Data Sources",
        path: DATA_SOURCE_ROUTES.datasources.path,
        onClick: handleItemClick(DATA_SOURCE_ROUTES.datasources.path)
      }
    ];

    if (hasDatasets) {
      dataSubItems.push({
        title: "Datasets",
        path: DATASET_ROUTES.datasetDefs.path,
        onClick: handleItemClick(DATASET_ROUTES.datasetDefs.path)
      });
    }

    items.push({
      title: "Data",
      icon: <Database size={17} />,
      closable: true,
      subItems: dataSubItems
    });
  }

  // Settings and Notifications are admin+-only for now (see isAdmin above), so once
  // we're here the plan-based filter below is the only thing narrowing subItems.
  if (isAdmin) {
    const settingsSubItems =
      user.workspace?.plan === "basic"
        ? SETTINGS_APP_MENU_ITEMS.subItems?.filter(item =>
            ["workspace", "accounts", "sso", "ai"].includes(item.path!)
          )
        : SETTINGS_APP_MENU_ITEMS.subItems;

    const settingsSub = [
      ...(settingsSubItems ?? []).map(settingsRoute => {
        return {
          ...settingsRoute,
          path: buildSubRoute("settings", settingsRoute.path!),
          onClick: handleItemClick(buildSubRoute("settings", settingsRoute.path!))
        };
      }),
      {
        key: "holidays",
        title: "Holidays",
        path: HOLIDAY_CALENDAR_ROUTES.holidays.path,
        onClick: handleItemClick(HOLIDAY_CALENDAR_ROUTES.holidays.path)
      }
    ];

    items.push({
      title: SETTINGS_APP_MENU_ITEMS.title,
      icon: SETTINGS_APP_MENU_ITEMS.icon,
      closable: true,
      subItems: settingsSub,
      divider: true
    });

    items.push({
      title: "Notifications",
      path: appRoutes.notifications.path,
      icon: <Icon name="bell" />,
      onClick: handleItemClick(appRoutes.notifications.path)
    });
  }

  if (user.role === UserRole.SuperAdmin) {
    items.push({
      title: "System",
      path: "/system",
      icon: <Icon name="templates" />,
      subItems: [
        {
          title: SYSTEM_ROUTES.actions.title,
          path: SYSTEM_ROUTES.actions.path,
          onClick: handleItemClick(SYSTEM_ROUTES.actions.path)
        }
      ]
    });
  }

  return items;
};
